import { Button, SkeletonLoader } from "@pepperstone/pepper-ui-kit";
import { useState } from "react";

import type { DropdownOption } from "../../DropDownV2";
import type { LeadTableFilterSubPropsV2 } from "./types";

import { Datepicker } from "../../Datepicker";
import { DropDownV2 } from "../../DropDownV2";
import { useIsMobile } from "../../hooks";
import { SearchInput } from "../../SearchInput";
import { useSearchPlaceHolder } from "../hooks/useSearchPlaceHolder";
import { useLeadsContextV2 } from "../providers/LeadsProviderV2";

export const DesktopFiltersV2 = ({
  handleDateRangePicker,
  handleExportButton,
  handleSearchInput,
  handleCountryDropdown,
  canViewPII,
  initialCountryCodes,
  translations: { translatedContent, language, countryCodeMapping },
  isBackoffice,
}: LeadTableFilterSubPropsV2) => {
  const isMobile = useIsMobile();
  const {
    state: {
      searchQuery,
      dateRange,
      selected: { countryCodes: selectedCountryCodes },
    },
    resources: {
      table: { data: tableData, status: tableStatus },
    },
  } = useLeadsContextV2();

  const searchPlaceHolder = useSearchPlaceHolder({
    canViewPII,
    redactedFields: tableData?.redactedFields,
    translatedContent,
  });

  const [search, setSearch] = useState(searchQuery);

  const countryCodeOptions = (initialCountryCodes ?? []).map((code) => ({
    value: code,
    label: countryCodeMapping.get(code) ?? code,
  }));

  const selectedCountryOptions =
    (selectedCountryCodes as string[])?.length === 0
      ? countryCodeOptions
      : ((selectedCountryCodes as string[]) ?? []).map((code) => ({
          value: code,
          label: countryCodeMapping.get(code) ?? code,
        }));

  const handleDropdownChange = (options: DropdownOption[]) => {
    handleCountryDropdown?.(options);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    handleSearchInput?.(value);
  };

  const isFiltersLoading = initialCountryCodes === undefined;

  if (isFiltersLoading) {
    return (
      <div className="flex gap-4">
        <div className="flex max-w-[462px] flex-1 flex-col gap-2 pt-1">
          <SkeletonLoader variant="rectangular" height={4} width={16} />
          <SkeletonLoader variant="rectangular" height={10} />
        </div>
        <div className="flex w-[240px] flex-col gap-2 pt-1">
          <SkeletonLoader variant="rectangular" height={4} width={16} />
          <SkeletonLoader variant="rectangular" height={10} />
        </div>
        <div className="flex w-[230px] flex-col gap-2 pt-1">
          <SkeletonLoader variant="rectangular" height={4} width={16} />
          <SkeletonLoader variant="rectangular" height={10} />
        </div>
        {!isBackoffice ? (
          <div className="ml-auto pt-8">
            <SkeletonLoader variant="rectangular" height={10} width={28} />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex gap-4">
      <div className="max-w-[462px] flex-1">
        <SearchInput
          value={search}
          onChange={handleSearchChange}
          label={translatedContent.searchLabel}
          placeholder={searchPlaceHolder}
        />
      </div>
      <div data-testid="desktop-date-picker" className="w-[240px]">
        <Datepicker
          inputId="dateRangeFilter"
          label={translatedContent.signupDatePickerLabel}
          value={{
            startDate: dateRange.from,
            endDate: dateRange.to,
          }}
          onChange={handleDateRangePicker}
          configs={{
            shortcuts: {
              today: translatedContent.today,
              yesterday: translatedContent.yesterday,
              past: (period) =>
                translatedContent.past ? translatedContent.past(period) : "",
              currentMonth: translatedContent.currentMonth,
              pastMonth: translatedContent.pastMonth,
            },
            footer: {
              cancel: translatedContent.cancel,
              apply: translatedContent.apply,
            },
          }}
          placeholder={translatedContent.datePickerPlaceholder}
          i18n={language}
          isMobile={isMobile}
        />
      </div>
      <div className="relative w-[230px]">
        <DropDownV2
          id="countryDropdown"
          options={countryCodeOptions}
          selected={selectedCountryOptions}
          label={translatedContent.countryFilterLabel ?? "Country"}
          isMobile={isMobile}
          dropDownLabels={translatedContent.countryDropDownLabels}
          handleApply={handleDropdownChange}
        />
      </div>
      {!isBackoffice ? (
        <div className="ml-auto pt-8">
          {tableStatus === "loading" ? (
            <SkeletonLoader variant="rectangular" height={10} width={28} />
          ) : (
            <Button
              as="button"
              type="button"
              size="ps-btn-md"
              variant="ps-btn-secondary"
              onClick={handleExportButton}
            >
              {translatedContent.exportCSVText}
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
};
